import { Navigate, type RouteObject } from 'react-router';

import {
  ApprovalsAdminLayout,
  AssetsLayout,
  AutomationAdminLayout,
  IntegrationsAdminLayout,
  ChangesLayout,
  CsatReadLayout,
  KbLayout,
  KbAiLayout,
  QueuesLayout,
  SettingsManageLayout,
  SlaAdminLayout,
  StatusLayout,
  TicketsLayout,
} from '@/features/auth/require-permission';
import { NotFoundPage } from '@/pages/not-found-page';
import { AUDIT_ADMIN_PATH, MAIL_ADMIN_PATH } from '@/platform/lib/admin';
import { PROFILE_PATH } from '@/platform/lib/paths';
import '@/products/helpdesk/profile-sections';
import '@/products/helpdesk/product-nav';
import { HELPDESK_ROOT, LEGACY_HELPDESK_AGENT_PATHS } from '@/products/helpdesk/helpdesk-paths';
import { LegacyHelpdeskRedirect } from '@/products/helpdesk/legacy-helpdesk-redirect';
import { PortalLayout } from '@/products/helpdesk/layouts/portal-layout';
import { ApprovalActionPage } from '@/products/helpdesk/pages/approval-action-page';
import { ApprovalsPage } from '@/products/helpdesk/pages/approvals-page';
import { AssetCreatePage } from '@/products/helpdesk/pages/asset-create-page';
import { AssetDetailPage } from '@/products/helpdesk/pages/asset-detail-page';
import { AssetsPage } from '@/products/helpdesk/pages/assets-page';
import { AutomationPage } from '@/products/helpdesk/pages/automation-page';
import { ChangeCalendarPage } from '@/products/helpdesk/pages/change-calendar-page';
import { CsatActionPage } from '@/products/helpdesk/pages/csat-action-page';
import { CsatAnalyticsPage } from '@/products/helpdesk/pages/csat-analytics-page';
import { HomePage } from '@/products/helpdesk/pages/home-page';
import { IntegrationsPage } from '@/products/helpdesk/pages/integrations-page';
import { KbArticleEditorPage } from '@/products/helpdesk/pages/kb-article-editor-page';
import { KbArticlePage } from '@/products/helpdesk/pages/kb-article-page';
import { KbAssistantPage } from '@/products/helpdesk/pages/kb-assistant-page';
import { KbHistoryPage } from '@/products/helpdesk/pages/kb-history-page';
import { KbOptimizePage } from '@/products/helpdesk/pages/kb-optimize-page';
import { KbPage } from '@/products/helpdesk/pages/kb-page';
import { PortalFaqPage } from '@/products/helpdesk/pages/portal-faq-page';
import { PortalHomePage } from '@/products/helpdesk/pages/portal-home-page';
import { PortalKbArticlePage } from '@/products/helpdesk/pages/portal-kb-article-page';
import { PortalKbPage } from '@/products/helpdesk/pages/portal-kb-page';
import { PortalTicketsPage } from '@/products/helpdesk/pages/portal-tickets-page';
import { QueuesPage } from '@/products/helpdesk/pages/queues-page';
import { SlaPage } from '@/products/helpdesk/pages/sla-page';
import { StatusPage } from '@/products/helpdesk/pages/status-page';
import { SystemModulesPage } from '@/products/helpdesk/pages/system-modules-page';
import { TicketCatalogPage } from '@/products/helpdesk/pages/ticket-catalog-page';
import { TicketCreatePage } from '@/products/helpdesk/pages/ticket-create-page';
import { TicketDetailPage } from '@/products/helpdesk/pages/ticket-detail-page';
import { TicketHistoryPage } from '@/products/helpdesk/pages/ticket-history-page';
import { TicketsPage } from '@/products/helpdesk/pages/tickets-page';

/** Helpdesk self-service for end users (`/portal/*`). */
export const helpdeskPortalRoute: RouteObject = {
  path: '/portal',
  Component: PortalLayout,
  children: [
    { index: true, Component: PortalHomePage },
    { path: 'tickets', Component: PortalTicketsPage },
    { path: 'tickets/neu', Component: TicketCreatePage },
    { path: 'tickets/:ticketRef/historie', Component: TicketHistoryPage },
    { path: 'tickets/:ticketRef', Component: TicketDetailPage },
    {
      path: 'wissen',
      Component: KbLayout,
      children: [
        { index: true, Component: PortalKbPage },
        { path: ':slug', Component: PortalKbArticlePage },
      ],
    },
    {
      path: 'faq',
      Component: KbLayout,
      children: [{ index: true, Component: PortalFaqPage }],
    },
    { path: 'einstellungen', element: <Navigate to={PROFILE_PATH} replace /> },
    { path: '*', Component: NotFoundPage },
  ],
};

/** Helpdesk agent UI, mounted under the shared app chrome (`RootLayout`). */
export const helpdeskAgentRoute: RouteObject = {
  path: HELPDESK_ROOT.slice(1),
  children: [
    { index: true, Component: HomePage },
    {
      path: 'status',
      Component: StatusLayout,
      children: [{ index: true, Component: StatusPage }],
    },
    {
      path: 'einstellungen/kategorien',
      Component: SettingsManageLayout,
      children: [{ index: true, Component: TicketCatalogPage }],
    },
    {
      path: 'einstellungen/system',
      Component: SettingsManageLayout,
      children: [{ index: true, Component: SystemModulesPage }],
    },
    {
      path: 'einstellungen/e-mail',
      element: <Navigate to={MAIL_ADMIN_PATH} replace />,
    },
    {
      path: 'warteschlangen',
      Component: QueuesLayout,
      children: [{ index: true, Component: QueuesPage }],
    },
    {
      path: 'tickets',
      Component: TicketsLayout,
      children: [
        { index: true, Component: TicketsPage },
        { path: 'neu', Component: TicketCreatePage },
        { path: ':ticketRef/historie', Component: TicketHistoryPage },
        { path: ':ticketRef', Component: TicketDetailPage },
      ],
    },
    {
      path: 'changes',
      Component: ChangesLayout,
      children: [{ index: true, Component: ChangeCalendarPage }],
    },
    {
      path: 'sla',
      Component: SlaAdminLayout,
      children: [{ index: true, Component: SlaPage }],
    },
    {
      path: 'freigaben',
      Component: ApprovalsAdminLayout,
      children: [{ index: true, Component: ApprovalsPage }],
    },
    {
      path: 'automatisierung',
      Component: AutomationAdminLayout,
      children: [{ index: true, Component: AutomationPage }],
    },
    {
      path: 'integrationen',
      Component: IntegrationsAdminLayout,
      children: [{ index: true, Component: IntegrationsPage }],
    },
    {
      path: 'wissen',
      Component: KbLayout,
      children: [
        { index: true, Component: KbPage },
        { path: 'neu', Component: KbArticleEditorPage },
        {
          path: 'assistent',
          Component: KbAiLayout,
          children: [{ index: true, Component: KbAssistantPage }],
        },
        {
          path: 'optimierung',
          Component: KbAiLayout,
          children: [{ index: true, Component: KbOptimizePage }],
        },
        { path: ':slug/bearbeiten', Component: KbArticleEditorPage },
        { path: ':slug/historie', Component: KbHistoryPage },
        { path: ':slug', Component: KbArticlePage },
      ],
    },
    {
      path: 'assets',
      Component: AssetsLayout,
      children: [
        { index: true, Component: AssetsPage },
        { path: 'neu', Component: AssetCreatePage },
        { path: ':assetId', Component: AssetDetailPage },
      ],
    },
    {
      path: 'audit/*',
      element: <Navigate to={AUDIT_ADMIN_PATH} replace />,
    },
    {
      path: 'auswertung',
      Component: CsatReadLayout,
      children: [{ index: true, Component: CsatAnalyticsPage }],
    },
    { path: '*', Component: NotFoundPage },
  ],
};

/** Token links from mails stay at the root (M0: global paths). */
export const helpdeskTokenRoutes: RouteObject[] = [
  { path: 'freigabe/:token', Component: ApprovalActionPage },
  { path: 'umfrage/:token', Component: CsatActionPage },
];

/** Old root-level agent URLs (incl. nested paths) → `/helpdesk/…`. */
export const helpdeskLegacyRedirectRoutes: RouteObject[] = [
  { path: 'einstellungen/e-mail', element: <Navigate to={MAIL_ADMIN_PATH} replace /> },
  { path: 'einstellungen/e-mail/*', element: <Navigate to={MAIL_ADMIN_PATH} replace /> },
  ...LEGACY_HELPDESK_AGENT_PATHS.flatMap((path) => [
    { path, Component: LegacyHelpdeskRedirect },
    { path: `${path}/*`, Component: LegacyHelpdeskRedirect },
  ]),
];
